import { AxiosResponse } from 'axios';

import { HttpTimeoutPriority } from 'utils/enums/http-timeout-priority';
import { prepareFiltersOfSelection, prepareQPFromFilters, serverAxios } from '../../http';
import {
  IDeleteShuttlesFilters,
  ICreateShuttleRequest,
  ICreateShuttleResponse,
  IUpdateShuttleRequest,
  IUpdateShuttleResponse,
  IDeleteShuttleResponse,
  IGetShuttlesFilters,
  IGetShuttlesResponse,
  IGetSingleShuttleResponse,
  IGetShuttleGroupsFilters,
  IGetShuttleGroupsResponse,
  IGetShuttlesByShuttleGroupIdFilters,
  IGetShuttlesByShuttleGroupIdResponse,
} from '../../../models/http/shuttles';
import { DataUploadModels, ModelsNames } from 'utils/enums/model-names';
import DataUploadBaseHttp from './utils/data-upload.http';
import { HTTP } from './core.http';

class ShuttleHttp extends DataUploadBaseHttp implements HTTP<IDeleteShuttlesFilters> {
  protected readonly route: DataUploadModels = ModelsNames.Shuttles;

  public async get(filters?: IGetShuttlesFilters, abortController?: AbortController): Promise<IGetShuttlesResponse['records']> {
    const queryParams = prepareQPFromFilters(filters || {});

    return serverAxios
      .get(`/${this.route}?${queryParams.toString()}`, {
        data: filters,
        timeout: HttpTimeoutPriority.Medium,
        signal: abortController?.signal,
      })
      .then((response: AxiosResponse<IGetShuttlesResponse>) => {
        return response.data.records;
      });
  }

  public async getGroups(
    filters?: IGetShuttleGroupsFilters,
    abortController?: AbortController,
  ): Promise<IGetShuttleGroupsResponse['suppliers']> {
    const queryParams = prepareQPFromFilters(filters || {});

    return serverAxios
      .get(`/${this.route}/groups?${queryParams.toString()}`, {
        timeout: HttpTimeoutPriority.Medium,
        signal: abortController?.signal,
      })
      .then((response: AxiosResponse<IGetShuttleGroupsResponse>) => {
        return response.data.suppliers;
      });
  }

  public async getShuttlesOfGroup(
    groupId: string,
    filters: IGetShuttlesByShuttleGroupIdFilters,
    abortController?: AbortController,
  ): Promise<IGetShuttlesByShuttleGroupIdResponse> {
    const queryParams = prepareQPFromFilters(filters || {});

    return serverAxios
      .get(`/${this.route}/groups/${groupId}/shuttles?${queryParams.toString()}`, {
        timeout: HttpTimeoutPriority.Medium,
        signal: abortController?.signal,
      })
      .then((response: AxiosResponse<IGetShuttlesByShuttleGroupIdResponse>) => response.data);
  }

  public async getById(id: string, abortController?: AbortController): Promise<IGetSingleShuttleResponse> {
    return serverAxios
      .get(`/${this.route}/single/${id}`, {
        timeout: HttpTimeoutPriority.Medium,
        signal: abortController?.signal,
      })
      .then((response: AxiosResponse<IGetSingleShuttleResponse>) => {
        return response.data;
      });
  }

  public async create(data: ICreateShuttleRequest): Promise<ICreateShuttleResponse> {
    return serverAxios
      .post(`/${this.route}`, data, {
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<ICreateShuttleResponse>) => {
        return response.data;
      });
  }

  public async update(id: number, shuttle: IUpdateShuttleRequest): Promise<IUpdateShuttleResponse> {
    return serverAxios
      .put(`/${this.route}/${id}`, shuttle, {
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IUpdateShuttleResponse>) => {
        return response.data;
      });
  }

  public async delete(ids: number[]): Promise<IDeleteShuttleResponse>;
  public async delete(filters: IDeleteShuttlesFilters): Promise<IDeleteShuttleResponse>;
  public async delete(filters: number[] | IDeleteShuttlesFilters): Promise<IDeleteShuttleResponse> {
    return serverAxios
      .delete(`/${this.route}`, {
        data: prepareFiltersOfSelection(filters),
      })
      .then((response: AxiosResponse<IDeleteShuttleResponse>) => {
        return response.data;
      });
  }
}

export default ShuttleHttp;
